"use client";

import Image from "next/image";
import React, { useCallback, useEffect, useState } from "react";
import iconArrowRight from "@/public/icon_arrowright.png";
import Link from "next/link";
import { format } from "date-fns";
import { isValidDate } from "@/utils/dateUtils";
import { useAuth } from "@/hooks/useAuth";
import obituaryService from "@/services/obituary-service";
import toast from "react-hot-toast";
import ModalLibrary from "./ModalLibrary";

const formatObituaryDate = (dateString) => {
  if (!dateString) return ""; // If date doesn't exist

  const date = new Date(dateString);

  // Otherwise, show full date
  return format(date, "yyyy");
};

// The death date is shown in full ("07.10.2026") when it is known; the birth is only a year.
const formatObituaryDeathDate = (dateString) => {
  if (!dateString) return "";
  return format(new Date(dateString), "dd.MM.yyyy");
};

const formatDateForDatetime = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return format(date, "yyyy");
};

const mergeListObituaryForModal = (row) => {
  const total =
    row?.totalCandles ??
    row?.candles?.totalCandles ??
    0;
  return {
    ...row,
    totalCandles: total,
    candles: {
      ...(row?.candles || {}),
      totalCandles: row?.candles?.totalCandles ?? total,
    },
  };
};

const ObituaryCard = ({
  data,
  index,
  mob,
  selectedCity,
  selectedRegion,
  iframed = false,
  onCandleBurned,
  // variant="message": the wider 500px card shown on top of a home page thank-you/memory message,
  // with a coloured border. Without it the card looks exactly as before.
  variant,
  borderColor,
  // Replaces the dates line; the thank-you messages let the author edit it.
  datesText,
}) => {
  const { user } = useAuth();
  const [isShowModal, setIsShowModal] = useState(false);
  const [select_id, setSelect_Id] = useState("");
  const [selectedImage, setSelectedImage] = useState("");
  const [myLastBurntCandleTime, setMyLastBurntCandleTime] = useState(null);

  /**
   * MINIMIZED REFACTOR (Task 4 Fix):
   * Previously, 'localData' was a separate state variable that required synchronization
   * with the 'data' prop via useEffect. This created a "double source of truth" bug.
   * Now, 'localData' is a pure derived constant. This ensures the component always 
   * reflects the latest 'data' prop without any sync lag or stale state, while
   * keeping code changes to the rest of the file minimal.
   */
  const localData = mergeListObituaryForModal(data);
  // "Poudarjena osmrtnica": a paid obituary gets its own colours until the funeral.
  const highlighted = Boolean(localData?.isHighlighted);

  // User-scoped key prevents a different user logging in on the same browser
  // from inheriting a previous user's cached cooldown.
  // Anonymous users keep the legacy key (no user ID available).
  const candleStorageKey = localData?.id
    ? user?.id
      ? `candle_${user.id}_${localData.id}`
      : `candle_${localData.id}`
    : null;

  useEffect(() => {
    if (!candleStorageKey) {
      setMyLastBurntCandleTime(null);
      return;
    }
    let stored = null;
    try {
      stored = localStorage.getItem(candleStorageKey);
    } catch {
      // Best-effort: blocked in Safari private mode and some embedded browsers.
    }
    setMyLastBurntCandleTime(stored);
  }, [candleStorageKey]);
  const formattedBirthDate = formatObituaryDate(localData?.birthDate);
  const formattedDeathDate = formatObituaryDeathDate(localData?.deathDate);
  const birthDisplayText =
    localData?.birthDate && isValidDate(localData?.birthDate) ?
      formattedBirthDate
    : localData?.birthYear != null ? String(localData.birthYear)
    : "";
  const deathDisplayText = isValidDate(localData?.deathDate) ?
      formattedDeathDate
    : localData?.deathYear != null ? String(localData.deathYear)
    : "";

  const imageUrl = localData?.image || "/user5.jpeg";

  const religionImages = {
    1: "/icon_cross.png",
    2: "/img_plus2.png",
    3: "/img_moon_star.png",
    4: "/img_plus3.png",
    5: "/img_star.png",
    6: "/icon_hands.png",
  };

  const buildHref = () => {
    const params = new URLSearchParams();

    if (selectedCity) {
      params.set("city", selectedCity);
    }
    if (selectedRegion) {
      params.set("region", selectedRegion);
    }

    const queryString = params.toString();
    return queryString ?
        `/m/${localData.slugKey}?${queryString}`
      : `/m/${localData.slugKey}`;
  };

  // A card without a memory page (the example messages on the home page) must not navigate anywhere.
  const hasMemoryPage = Boolean(localData?.slugKey);
  const href = hasMemoryPage ? buildHref() : "#";
  const linkTarget = iframed ? "_blank" : "_self";
  const linkProps =
    hasMemoryPage ? {} : { onClick: (e) => e.preventDefault(), tabIndex: -1, "aria-disabled": true };

  const candleCount =
    localData?.totalCandles ??
    localData?.candles?.totalCandles ??
    0;

  const openCandleModal = () => {
    setIsShowModal(true);
  };

  const burnCandle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const candleData = { userId: user ? user.id : null };
    try {
      const response = await obituaryService.burnCandle(localData?.id, candleData);
      if (response?.candle?.id) {
        toast.success("Svečka je prižgana");
        const burnedAt = response.candle?.createdAt || new Date().toISOString();
        try {
          if (candleStorageKey) localStorage.setItem(candleStorageKey, burnedAt);
        } catch {
          // Best-effort persistence only.
        }
        setMyLastBurntCandleTime(burnedAt);
        openCandleModal();
        setSelect_Id("3");
        if (onCandleBurned) {
          onCandleBurned(localData.id);
        }
      }
    } catch (error) {
      console.error("Failed to burn candle:", error);
      if (error?.status === 409) {
        let stored = null;
        try {
          if (candleStorageKey) stored = localStorage.getItem(candleStorageKey);
        } catch {
          // Best-effort lookup only.
        }
        if (stored) {
          setMyLastBurntCandleTime(stored);
        } else if (localData?.slugKey) {
          try {
            const detail = await obituaryService.getMemory({ slugKey: localData.slugKey });
            const serverMyBurnTime = detail?.obituary?.candles?.myLastBurntCandleTime;
            if (serverMyBurnTime) {
              try { if (candleStorageKey) localStorage.setItem(candleStorageKey, serverMyBurnTime); } catch { /* Best-effort */ }
              setMyLastBurntCandleTime(serverMyBurnTime);
            }
          } catch {
            // Best-effort fallback.
          }
        }
        openCandleModal();
        setSelect_Id("3");
        toast.error("Vsakih 24 ur lahko prižgeš eno svečko");
      }
    }
  };

  return (
    <>
      <ModalLibrary
        isShowModal={isShowModal}
        setIsShowModal={setIsShowModal}
        select_id={select_id}
        set_Id={setSelect_Id}
        selectedImage={selectedImage}
        data={{
          ...localData,
          candles: {
            ...(localData?.candles || {}),
            myLastBurntCandleTime:
              myLastBurntCandleTime ?? localData?.candles?.myLastBurntCandleTime,
          },
        }}
        updateObituary={onCandleBurned || (() => {})}
      />
      <div
        className={
          variant === "message" ?
            `w-full max-w-[500px] mobile:h-[126px] tablet:h-[170px] desktop:h-[170px] border
       shadow-custom-light-dark-box
        ${highlighted ? "obit-card-highlight" : "bg-gradient-to-br from-[#E3EAEF] to-[#F3F6F8]"} rounded-lg flex justify-start items-center relative`
          : `mobile:w-[100%] mobile:max-w-[400px] tablet:w-[466px] desktop:w-[466px] 
      mobile:h-[126px] tablet:h-[170px] desktop:h-[170px]  border-2
       ${highlighted ? "obit-card-highlight" : "border-white bg-gradient-to-br from-[#E3EAEF] to-[#F3F6F8]"} shadow-custom-light-dark-box
        rounded-lg flex justify-start items-center relative`
        }
        style={variant === "message" && !highlighted ? { borderColor } : undefined}
      >
        <div className="flex mobile:w-[100%] mobile:px-3 tablet:px-4 desktop:px-4 w-full">
          <div className="mobile:w-[100%] tablet:w-[420.33px] desktop:w-[428px] flex flex-1 min-w-0">
            <Link
              href={href}
              {...linkProps}
              target={linkTarget}
              className="rounded-xl mobile:mr-[17.33px] tablet:mr-[26px] desktop:mr-[29px] shrink-0
          shadow-custom-light-dark-box-image p-1 bg-gradient-to-br from-[#E3E8EC] to-[#FFFFFF] self-start"
            >
              <Image
                src={imageUrl}
                alt={`Fotografija ${[localData.name || "", localData.sirName || ""].filter(Boolean).join(" ") || "osoba"} - Osmrtnica.com`}
                width={1000}
                height={1000}
                className="mobile:h-[98.53px] tablet:h-[130px]
               desktop:h-[130px] mobile:w-[72px] tablet:w-[95px] desktop:w-[95px] bg-center  rounded-lg"
              />
            </Link>

            <div className="flex-1 flex flex-col min-w-0 truncate overflow-hidden">
              <Link
                href={href}
                {...linkProps}
                target={linkTarget}
                className="flex flex-col items-start min-w-0 whitespace-nowrap"
              >
                <div
                  className="flex flex-row w-full items-start mobile:pt-[0px] tablet:pt-[4px]
             desktop:pt-[4px] tablet:pr-[2px] desktop:pr-[2px] "
                >
                  <div className="flex flex-1 flex-col min-w-0">
                    <span
                      className="font-variation-customOpt24 text-left desktop:text-[24px]
                   tablet:text-[24px] mobile:text-[16px]  text-[#1E2125] leading-[28.13px]"
                    >
                      {localData.name} {localData.sirName}
                    </span>
                    <p
                      className="block font-variation-customOpt14 tablet:font-variation-customOpt16 
                  desktop:font-variation-customOpt16 text-left desktop:mt-[16px] tablet:mt-[16px]  
                  desktop:text-[16px] tablet:text-[16px] mobile:text-[14px]  text-[#414141] leading-[24px]"
                    >
                      {(() => {
                        if (datesText != null) return datesText;
                        if (birthDisplayText) {
                          return (
                            <>
                              {localData?.birthDate && isValidDate(localData.birthDate) ?
                                <time dateTime={formatDateForDatetime(localData.birthDate)}>
                                  {birthDisplayText}
                                </time>
                              : <>{birthDisplayText}</>}{" "}
                              –{" "}
                              {(localData?.deathDate && isValidDate(localData.deathDate)) ||
                              localData?.deathYear != null ?
                                <time
                                  dateTime={
                                    isValidDate(localData?.deathDate) ?
                                      formatDateForDatetime(localData.deathDate)
                                    : (localData?.deathYear ?? "")
                                  }
                                >
                                  {deathDisplayText}
                                </time>
                              : <>{deathDisplayText}</>}
                            </>
                          );
                        }
                        if (localData?.ageInYears != null && localData?.ageInYears > 0) {
                          return isValidDate(localData?.deathDate) ||
                            localData?.deathYear != null ?
                              <time
                                dateTime={
                                  isValidDate(localData?.deathDate) ?
                                    formatDateForDatetime(localData.deathDate)
                                  : (localData?.deathYear ?? "")
                                }
                              >
                                {deathDisplayText} (v {localData.ageInYears}. letu)
                              </time>
                            : <>{deathDisplayText} (v {localData.ageInYears}. letu)</>;
                        }
                        return isValidDate(localData?.deathDate) ||
                          localData?.deathYear != null ?
                            <time
                              dateTime={
                                isValidDate(localData?.deathDate) ?
                                  formatDateForDatetime(localData.deathDate)
                                : (localData?.deathYear ?? "")
                              }
                            >
                              {deathDisplayText}
                            </time>
                          : <>{deathDisplayText}</>;
                      })()}
                    </p>
                    <p className="font-variation-customOpt14 tablet:font-variation-customOpt16 desktop:font-variation-customOpt16 text-left desktop:text-[16px] tablet:text-[16px] mobile:text-[14px]  text-[#414141] leading-[24px] tablet:mt-[4px] desktop:mt-[4px]">
                      {localData.location}
                    </p>
                  </div>
                </div>
              </Link>

              <div
                className={`flex mobile:h-[14px] tablet:h-6 desktop:h-6 w-full justify-end items-center
          mobile:mt-[17.58px] tablet:mt-[7.5px] desktop:mt-[7.5px]          
             mobile:pr-[0px] tablet:pr-[2px] desktop:pr-[2px]`}
              >
                <button
                  type="button"
                  onClick={burnCandle}
                  disabled={!localData?.id}
                  className="flex cursor-pointer items-center gap-1 rounded px-0.5 py-0.5 text-[#7C7C7C] hover:opacity-80 mobile:font-variation-customOpt12 tablet:font-variation-customOpt14 desktop:font-variation-customOpt14 mobile:text-[12px] tablet:text-[14px] desktop:text-[14px] bg-transparent border-0"
                  aria-label={`Prižgi svečko, skupaj ${candleCount}`}
                >
                  <Image
                    src="/candle.png"
                    alt=""
                    width={20}
                    height={24}
                    className="mobile:h-[18px] mobile:w-[15px] tablet:h-[22px] tablet:w-[18px] desktop:h-[22px] desktop:w-[18px]"
                  />
                  <span className="tabular-nums">{candleCount}</span>
                </button>
                <Link
                  href={href}
                  {...linkProps}
                  target={linkTarget}
                  className="flex items-center shrink-0 ml-0.5"
                  aria-label="Odpri osmrtnico"
                >
                  <Image
                    src={iconArrowRight}
                    alt=""
                    width={1000}
                    height={1000}
                    className="mobile:h-[15.24px] mobile:w-[15.24px] tablet:h-[24px] tablet:w-[24px] desktop:h-[24px] desktop:w-[24px]"
                  />
                </Link>
              </div>
            </div>
          </div>
          {religionImages[localData?.symbol] && !mob && (
            <div
              className={`absolute z-40 flex justify-end 
              ${variant === "message" ? "mobile:w-[calc(100%-24px)]" : "mobile:w-[171px]"} 
             tablet:w-[420.33px] tablet:pt-[3px]
              desktop:w-[428px] desktop:pt-[3px]  desktop:pr-2
              `}
            >
              <Image
                src={religionImages[localData?.symbol]}
                alt="Verski simbol"
                width={1000}
                height={1000}
                className={`w-[51px] ${localData.symbol === "3" ? "h-[50px]" : "h-[55px]"
                  }`}
              />
            </div>
          )}
          {religionImages[localData?.symbol] && mob && (
            <div
              className=" absolute z-45 flex justify-end 
              w-[171px]  mobile:mt-[28.26px] pr-[3px]
              mobile:w-[263px] 
              "
            >
              <Image
                src={religionImages[localData.symbol]}
                alt="Verski simbol"
                width={500}
                height={500}
                className={` w-[37.66px] tablet:h-[65px] tablet:w-[51px] ${localData.symbol === "3" ? "h-[40px]" : "h-[48px]"
                  }`}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ObituaryCard;
